import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  FormControl,
  Paper,
  InputAdornment,
  Fade,
} from "@mui/material";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import Swal from "sweetalert2";
import { postJsonData } from "../network/ApiController";
import ApiEndpoints from "../network/ApiEndPoints";
import { apiErrorToast, okSuccessToast } from "../utils/ToastUtil";
import CustomTabs from "./CustomTabs";

const PgOrder = () => {
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [amount, setAmount] = useState("");

  const PRIMARY = "#E4097F";
  const PRIMARY_DARK = "#D81B78";
  const SOFT_BORDER = "#F3B4D3";

  const tabConfig = [
    {
      label: "Gold",
      type: "gold",
      emoji: "🥇",
      subtitle: "Faster settlements, three times a day",
      minAmount: 1,
      maxAmount: 49999,
    },
    {
      label: "Silver",
      type: "silver",
      emoji: "🥈",
      subtitle: "Instant access with real-time settlements",
      minAmount: 1,
      maxAmount: 29999,
    },
    {
      label: "Diamond",
      type: "diamond",
      emoji: "💎",
      subtitle: "Premium settlement by next day",
      minAmount: 1,
      maxAmount: 39999,
    },
  ];

  const selectedTab = tabConfig[tabValue];
  const numericAmount = Number(amount);

  const isAmountValid =
    amount !== "" &&
    !isNaN(numericAmount) &&
    numericAmount >= selectedTab.minAmount &&
    numericAmount <= selectedTab.maxAmount;

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setAmount("");
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;

    // Empty allow
    if (value === "") {
      setAmount("");
      return;
    }

    // Only digits allow
    if (/^\d+$/.test(value)) {
      const numericValue = Number(value);

      // Max se upar input block
      if (numericValue <= selectedTab.maxAmount) {
        setAmount(value);
      }
    }
  };

  const handleCreatePg = () => {
    const selectedType = selectedTab.type;
    const selectedLabel = selectedTab.label;
    const minAmount = selectedTab.minAmount;
    const maxAmount = selectedTab.maxAmount;

    // Required validation
    if (!amount || isNaN(numericAmount)) {
      Swal.fire({
        icon: "error",
        title: "Invalid Amount",
        text: `Please enter a valid amount for ${selectedLabel}`,
      });
      return;
    }

    // Range validation
    if (numericAmount < minAmount || numericAmount > maxAmount) {
      Swal.fire({
        icon: "warning",
        title: "Amount Limit Exceeded",
        text: `${selectedLabel} payment amount must be between ₹${minAmount.toLocaleString(
          "en-IN"
        )} and ₹${maxAmount.toLocaleString("en-IN")}`,
      });
      return;
    }

    const payload = {
      amount: numericAmount,
      type: selectedType,
    };

    console.log("Payload:", payload);

    postJsonData(
      ApiEndpoints.CREATE_PG,
      payload,
      setIsLoading,
      (res) => {
        console.log("API Success Response:", res);

        if (res) {
          const successMessage =
            res?.data?.response?.message ||
            res?.data?.message ||
            res?.message ||
            `Redirecting to ${selectedLabel} payment...`;

          const paymentLink =
            res?.data?.response?.link ||
            res?.data?.link ||
            res?.link;

          if (paymentLink) {
            okSuccessToast(successMessage);
            window.open(paymentLink, "_blank", "noopener,noreferrer");
          } else {
            apiErrorToast(
              res?.data?.response?.message ||
                res?.data?.message ||
                res?.message ||
                "Payment link not found"
            );
          }
        } else {
          apiErrorToast("Payment initiation failed");
        }
      },
      (error) => {
        console.error("API Error:", error);

        const errorMessage =
          error?.response?.data?.response?.message ||
          error?.response?.data?.message ||
          error?.message ||
          `Failed to initiate ${selectedLabel} Payment Gateway`;

        apiErrorToast(errorMessage);

        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMessage,
        });
      }
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && isAmountValid && !isLoading) {
      handleCreatePg();
    }
  };

  const renderPgForm = (tab) => (
    <Fade in timeout={400}>
      <Box
        sx={{
          minHeight: "72vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          px: 2,
          py: 4,
          background: "linear-gradient(180deg, #fff 0%, #fff7fb 100%)",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 520,
            borderRadius: "28px",
            overflow: "hidden",
            boxShadow: "0 14px 40px rgba(228, 9, 127, 0.15)",
            border: `1px solid ${SOFT_BORDER}`,
            background: "#fff",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
              color: "#fff",
              px: 4,
              py: 4.5,
              textAlign: "center",
              position: "relative",
            }}
          >
            <Typography sx={{ fontSize: "38px", mb: 1 }}>
              {tab.emoji}
            </Typography>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                letterSpacing: 0.3,
                fontSize: { xs: "1.7rem", sm: "2rem" },
              }}
            >
              {tab.label} Payment
            </Typography>

            {/* Highlighted Subtitle */}
            <Box
              sx={{
                mt: 2,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2.2,
                py: 1.1,
                borderRadius: "50px",
                background: "rgba(255,255,255,0.18)",
                border: "1px solid #fff",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                boxShadow: "0 8px 22px rgba(0,0,0,0.12)",
                maxWidth: "100%",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "12.5px", sm: "16px" },
                  fontWeight: 700,
                  color: "#fff",
                  letterSpacing: 0.3,
                  lineHeight: 1.8,
                  textAlign: "center",
                }}
              >
                {tab.subtitle}
              </Typography>
            </Box>
          </Box>

          {/* Body */}
          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            <Typography
              variant="body1"
              sx={{
                mb: 1.5,
                color: "text.secondary",
                textAlign: "center",
                fontSize: "15px",
              }}
            >
              Enter the amount to continue with{" "}
              <Box component="span" sx={{ fontWeight: 700, color: PRIMARY }}>
                {tab.label}
              </Box>{" "}
              payment.
            </Typography>

            {/* Limit Info */}
            <Typography
              sx={{
                mb: 3,
                textAlign: "center",
                fontSize: "13px",
                fontWeight: 600,
                color: PRIMARY_DARK,
                background: "#FFF0F7",
                border: `1px dashed ${SOFT_BORDER}`,
                borderRadius: "12px",
                py: 1,
                px: 2,
              }}
            >
              Allowed Range: ₹{tab.minAmount.toLocaleString("en-IN")} - ₹
              {tab.maxAmount.toLocaleString("en-IN")}
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <TextField
                label="Enter Amount"
                type="number"
                value={amount}
                onChange={handleAmountChange}
                onKeyDown={handleKeyDown}
                inputProps={{
                  min: tab.minAmount,
                  max: tab.maxAmount,
                }}
                fullWidth
                size="medium"
                variant="outlined"
                error={amount !== "" && !isAmountValid}
                helperText={
                  amount !== "" && !isAmountValid
                    ? `Amount must be between ₹${tab.minAmount.toLocaleString(
                        "en-IN"
                      )} and ₹${tab.maxAmount.toLocaleString("en-IN")}`
                    : `Max allowed for ${tab.label}: ₹${tab.maxAmount.toLocaleString(
                        "en-IN"
                      )}`
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CurrencyRupeeIcon
                        sx={{ color: PRIMARY }}
                        fontSize="small"
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "16px",
                    background: "#fafafa",
                    fontSize: "16px",
                    height: "56px",
                  },
                  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                    borderColor: PRIMARY,
                    borderWidth: "2px",
                  },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: PRIMARY,
                  },
                  "& .MuiFormHelperText-root": {
                    fontWeight: 500,
                    ml: 1,
                    mt: 1,
                  },
                }}
              />
            </FormControl>

            <Button
              variant="contained"
              onClick={handleCreatePg}
              disabled={isLoading || !isAmountValid}
              fullWidth
              sx={{
                py: 1.7,
                borderRadius: "16px",
                fontWeight: 800,
                textTransform: "none",
                fontSize: "15px",
                background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_DARK} 100%)`,
                boxShadow: "0 10px 24px rgba(228, 9, 127, 0.25)",
                transition: "all 0.25s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 14px 30px rgba(228, 9, 127, 0.35)",
                },
                "&:disabled": {
                  background: "#d9d9d9",
                  color: "#fff",
                  boxShadow: "none",
                  cursor: "not-allowed",
                },
              }}
            >
              {isLoading ? "Processing..." : `Create ${tab.label} PG`}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Fade>
  );

  const tabs = tabConfig.map((tab) => ({
    label: tab.label,
    content: renderPgForm(tab),
  }));

  return (
    <Box sx={{ width: "100%" }}>
      <CustomTabs
        tabs={tabs}
        value={tabValue}
        onChange={handleTabChange}
        sx={{ minHeight: "58px" }}
      />
    </Box>
  );
};

export default PgOrder;